/**
 * DriverSubscription — driver self-serve membership/subscription page (#10).
 *
 * Display/billing only. A driver can subscribe to a weekly or monthly access
 * plan (Stripe Checkout in subscription mode), see their status + next billing
 * date, and cancel (at period end). Subscription status NEVER affects going
 * online, accepting jobs, or any dispatch/lifecycle behaviour — it coexists
 * with the separate daily firm fee.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Loader2, CalendarClock, CreditCard, AlertTriangle, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const gbp = (n) => `£${Number(n ?? 0).toFixed(2)}`;
const intervalLabel = (i) => (i === "week" ? "Weekly" : i === "month" ? "Monthly" : i);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_META = {
  active: { label: "Active", cls: "bg-[#DCFCE7] text-[#166534] border-[#16A34A]" },
  past_due: { label: "Past due", cls: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]" },
  canceled: { label: "Cancelled", cls: "bg-[#FEE2E2] text-[#991B1B] border-[#EF4444]" },
  incomplete: { label: "Incomplete", cls: "bg-secondary text-muted-foreground border-border" },
};

export default function DriverSubscription() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login");
    else if (user.role !== "driver") navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/subscription");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  // Handle the return-from-Stripe redirect (?sub_session_id / ?sub_cancel).
  useEffect(() => {
    if (!user) return;
    const sessionId = params.get("sub_session_id");
    const cancelled = params.get("sub_cancel");
    if (cancelled) {
      toast.info("Subscription checkout cancelled");
      setParams({}, { replace: true });
      load();
      return;
    }
    if (sessionId) {
      setPolling(true);
      (async () => {
        try {
          const { data } = await api.get(`/driver/subscription/status/${sessionId}`);
          setData(data);
          if (data?.subscription?.status === "active") toast.success("You're subscribed! 🎉");
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setPolling(false);
          setParams({}, { replace: true });
        }
      })();
      return;
    }
    load();
  }, [user, params, setParams, load]);

  const subscribe = async (planId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/subscription/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't start checkout");
    } catch (e) {
      // A 502/gateway error (or any non-JSON body) means the payment service
      // is unreachable — show a friendly message rather than raw gateway HTML.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(
        status === 502 || typeof e?.response?.data !== "object"
          ? "Could not start subscription checkout. Please try again later."
          : (detail || formatApiError(e))
      );
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/driver/subscription/cancel");
      setData(data);
      toast.success("Subscription will end at the period close");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const manageBilling = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/subscription/billing-portal", {
        return_url: window.location.href,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't open the billing portal");
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 502 || typeof e?.response?.data !== "object"
          ? "Could not open the billing portal. Please try again later."
          : (e?.response?.data?.detail || formatApiError(e))
      );
    } finally {
      setBusy(false);
    }
  };

  const sub = data?.subscription;
  const plans = data?.plans || [];
  const hasActive = sub && ["active", "past_due"].includes(sub.status);
  const meta = STATUS_META[sub?.status] || STATUS_META.incomplete;

  return (
    <div className="min-h-screen bg-background" data-testid="driver-subscription-page">
      <div className="sticky top-0 z-10 bg-white border-b border-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/driver")} className="p-1 hover:bg-secondary rounded" data-testid="subscription-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-heading text-lg font-black tracking-tight">MEMBERSHIP</h1>
          <p className="text-xs text-muted-foreground">Your driver access subscription</p>
        </div>
      </div>

      <div className="max-w-xl mx-auto p-4 space-y-4">
        {polling && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6" data-testid="subscription-polling">
            <Loader2 className="w-4 h-4 animate-spin" /> Confirming your subscription…
          </div>
        )}

        {/* Coexistence note — subscriptions do not affect the daily firm fee. */}
        <div className="text-xs text-muted-foreground bg-secondary/40 border border-border p-3">
          This is a separate access membership. It does not replace your daily firm fee and does not affect going online or accepting jobs.
        </div>

        {hasActive ? (
          <div className="bg-white border border-border p-5 space-y-4" data-testid="subscription-active-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-[#16A34A]" />
                <span className="font-heading text-lg font-black">{sub.plan_name}</span>
              </div>
              <span className={`text-xs font-bold px-2 py-1 border ${meta.cls}`} data-testid="subscription-status-badge">{meta.label}</span>
            </div>
            <div className="grid grid-cols-2 gap-px bg-border border border-border">
              <div className="bg-white p-3">
                <div className="label-mini text-muted-foreground">PLAN</div>
                <div className="font-semibold mt-0.5">{intervalLabel(sub.interval)} · {gbp(sub.amount_gbp)}</div>
              </div>
              <div className="bg-white p-3">
                <div className="label-mini text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3 h-3" /> NEXT BILLING</div>
                <div className="font-semibold mt-0.5" data-testid="subscription-next-billing">{fmtDate(sub.current_period_end)}</div>
              </div>
            </div>

            {sub.status === "past_due" && (
              <div className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#F59E0B] p-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Your last payment failed — please update your card to keep your membership.
              </div>
            )}

            {sub.cancel_at_period_end ? (
              <div className="text-xs text-muted-foreground bg-secondary/40 border border-border p-2" data-testid="subscription-cancel-pending">
                Your membership will end on {fmtDate(sub.current_period_end)} and won't renew.
              </div>
            ) : (
              <Button onClick={cancel} disabled={busy} variant="outline" className="w-full border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white" data-testid="subscription-cancel-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1.5" /> Cancel membership</>}
              </Button>
            )}

            <Button onClick={manageBilling} disabled={busy} variant="outline" className="w-full" data-testid="subscription-manage-billing-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Manage billing &amp; invoices</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="subscription-plans">
            {plans.length === 0 ? (
              <div className="bg-white border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="subscription-no-plans">
                No membership plans are available right now. Please check back later.
              </div>
            ) : (
              plans.map((p) => (
                <div key={p.id} className="bg-white border border-border p-5 flex items-center justify-between" data-testid={`subscription-plan-${p.id}`}>
                  <div>
                    <div className="font-heading text-lg font-black">{p.name}</div>
                    <div className="text-sm text-muted-foreground">{intervalLabel(p.interval)} · billed every {p.interval}</div>
                    <div className="font-heading text-2xl font-black mt-1">{gbp(p.amount_gbp)}<span className="text-sm font-normal text-muted-foreground">/{p.interval}</span></div>
                  </div>
                  <Button onClick={() => subscribe(p.id)} disabled={busy} data-testid={`subscribe-btn-${p.id}`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Subscribe</>}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
